import {
    Grid2X2, Link2, Lock, Maximize, Minimize, PictureInPicture2,
    Square, Unlock, Users, Pencil
} from 'lucide-react'
import { MriButton, MriSegmentedTabs, cn } from '@mriqbox/ui-kit'
import type { SessionStatus } from '@/hooks/useRoomSession'
import type { ViewMode } from '@/components/Stage'

export interface RoomTopbarProps {
    brand: string
    code: string
    /** Nome da sala fixa. Sala do momento nao tem nome, so codigo. */
    roomName?: string
    /** So o dono da sala fixa recebe: abre a edicao sem sair da sala. */
    onEditRoom?: () => void
    participants: number
    status: SessionStatus
    locked: boolean
    isHost: boolean
    mode: ViewMode
    /** Desabilita o seletor quando ha menos de duas telas. */
    modeEnabled: boolean
    isFullscreen: boolean
    /** Janela flutuante disponivel neste navegador. */
    pipAvailable: boolean
    pipOpen: boolean
    onTogglePip: () => void
    onModeChange: (mode: ViewMode) => void
    onToggleLock: () => void
    onToggleFullscreen: () => void
    /**
     * Abre o painel da sala como gaveta. So existe no celular: no desktop o
     * painel e coluna fixa e nunca precisou de quem o abrisse.
     */
    onOpenParticipants?: () => void
}

const STATUS_LABEL: Record<SessionStatus, string> = {
    idle: 'Desconectado',
    connecting: 'Conectando',
    connected: 'Conectado',
    reconnecting: 'Reconectando'
}

export function RoomTopbar({
    brand,
    code,
    roomName,
    onEditRoom,
    participants,
    status,
    locked,
    isHost,
    mode,
    modeEnabled,
    isFullscreen,
    pipAvailable,
    pipOpen,
    onTogglePip,
    onModeChange,
    onToggleLock,
    onToggleFullscreen,
    onOpenParticipants
}: RoomTopbarProps) {
    return (
        <header className="flex h-14 shrink-0 items-center gap-2 border-b border-border/70 bg-card/50 px-2 backdrop-blur sm:gap-3 sm:px-4">
            {/* A marca e a primeira a sair: quem esta na sala ja sabe onde esta,
                e o codigo ao lado e o que se compartilha. */}
            <span className="hidden text-sm font-extrabold tracking-tight text-foreground sm:inline">{brand}</span>

            <span className="flex min-w-0 items-center gap-1.5 rounded-md border border-primary/30 bg-primary/10 px-2 py-1">
                <Link2 className="h-3 w-3 shrink-0 text-primary" />
                {/*
                  * Na sala fixa o nome vem antes: e por ele que as pessoas
                  * reconhecem a sala. O codigo continua ali porque e ele que se
                  * digita e se compartilha.
                  */}
                {roomName && (
                    <>
                        <span className="max-w-[7rem] truncate text-xs font-semibold text-foreground sm:max-w-[14rem]" title={roomName}>
                            {roomName}
                        </span>
                        <span className="text-primary/40" aria-hidden>·</span>
                    </>
                )}
                <span className="font-mono tracking-[0.2em] text-xs font-semibold text-primary">{code}</span>
            </span>

            {/*
              * O dono costuma perceber que quer trocar o nome justamente quando
              * esta dentro da sala; manda-lo sair pra isso seria um recado
              * estranho.
              */}
            {onEditRoom && (
                <button
                    type="button"
                    onClick={onEditRoom}
                    title="Editar nome e PIN desta sala"
                    aria-label="Editar esta sala"
                    className="flex h-7 w-7 items-center justify-center rounded-lg border border-border/60 text-muted-foreground transition-colors hover:border-primary/40 hover:bg-primary/10 hover:text-primary"
                >
                    <Pencil className="h-3.5 w-3.5" />
                </button>
            )}

            <div className="ml-auto flex items-center gap-1.5 sm:gap-2">
                <MriSegmentedTabs
                    value={mode}
                    onChange={(id) => onModeChange(id as ViewMode)}
                    className={cn('hidden sm:flex', !modeEnabled && 'pointer-events-none opacity-40')}
                    items={[
                        { id: 'mosaico', label: 'Mosaico', icon: Grid2X2 },
                        { id: 'destaque', label: 'Destaque', icon: Square }
                    ]}
                />

                <MriButton
                    variant="outline"
                    size="sm"
                    onClick={onToggleLock}
                    disabled={!isHost}
                    title={isHost ? 'Trancar a sala pra novas entradas' : 'So o host pode trancar'}
                >
                    {locked
                        ? <Lock className="h-3.5 w-3.5 text-amber-400 lg:mr-2" />
                        : <Unlock className="h-3.5 w-3.5 lg:mr-2" />}
                    <span className="hidden lg:inline">{locked ? 'Trancada' : 'Trancar'}</span>
                </MriButton>

                <span
                    className="hidden items-center gap-1.5 rounded-md border border-border/70 px-2 py-1 text-xs text-muted-foreground md:flex"
                    title={`${participants} pessoa(s) na sala`}
                >
                    <Users className="h-3.5 w-3.5" />
                    {participants}
                </span>

                {onOpenParticipants && (
                    <button
                        type="button"
                        onClick={onOpenParticipants}
                        title="Abrir o painel da sala"
                        aria-label={`Abrir o painel da sala (${participants} na sala)`}
                        className="flex items-center gap-1.5 rounded-md border border-border/70 px-2 py-1 text-xs text-muted-foreground transition-colors hover:text-foreground md:hidden"
                    >
                        <Users className="h-3.5 w-3.5" />
                        {participants}
                    </button>
                )}

                {/* Janela flutuante: fica por cima de qualquer aplicativo, pra
                    acompanhar a sala enquanto se trabalha em outra janela. */}
                {pipAvailable && (
                    <MriButton
                        variant={pipOpen ? 'secondary' : 'ghost'}
                        size="icon"
                        className="h-8 w-8"
                        onClick={onTogglePip}
                        title={pipOpen ? 'Trazer a sala de volta' : 'Abrir em janela flutuante'}
                        aria-label={pipOpen ? 'Trazer a sala de volta' : 'Abrir em janela flutuante'}
                        aria-pressed={pipOpen}
                    >
                        <PictureInPicture2 className="h-4 w-4" />
                    </MriButton>
                )}

                <MriButton
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8"
                    onClick={onToggleFullscreen}
                    title={isFullscreen ? 'Sair da tela cheia' : 'Tela cheia'}
                    aria-label={isFullscreen ? 'Sair da tela cheia' : 'Tela cheia'}
                >
                    {isFullscreen ? <Minimize className="h-4 w-4" /> : <Maximize className="h-4 w-4" />}
                </MriButton>

                {/* role="status" + aria-live: o ponto colorido sozinho nao dizia
                    nada pra leitor de tela, e o texto ficava so em telas grandes —
                    no celular, reconectar nao mostrava nada pra ninguem. */}
                <span className="flex items-center gap-1.5 text-xs" role="status" aria-live="polite">
                    <span
                        className={cn(
                            'h-2 w-2 shrink-0 rounded-full',
                            status === 'connected' && 'bg-emerald-400',
                            status === 'connecting' && 'animate-pulse bg-amber-400',
                            status === 'reconnecting' && 'animate-pulse bg-amber-400',
                            status === 'idle' && 'bg-muted-foreground'
                        )}
                        aria-hidden
                    />
                    <span className="text-muted-foreground">{STATUS_LABEL[status]}</span>
                </span>
            </div>
        </header>
    )
}
